import { Circle, CircleDashed, CheckCircle2 } from 'lucide-react';
import { formatDay } from './taskDates';

/**
 * The task vocabulary, in one place.
 *
 * Colours, labels and ordering for every task field live here so the Mission
 * Control board, the chat side panel and the agent changeset all describe a
 * "high priority" task the same way.
 */

export const STATUS_META = {
    'todo': {
        label: 'To Do',
        short: 'todo',
        icon: Circle,
        chip: 'bg-white/5 text-muted-foreground border-white/10',
        dot: 'bg-muted-foreground',
        rail: 'bg-white/20',
    },
    'in-progress': {
        label: 'In Progress',
        short: 'progress',
        icon: CircleDashed,
        chip: 'bg-accent/15 text-accent border-accent/30',
        dot: 'bg-accent',
        rail: 'bg-accent',
    },
    'done': {
        label: 'Done',
        short: 'done',
        icon: CheckCircle2,
        chip: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
        dot: 'bg-emerald-500',
        rail: 'bg-emerald-500',
    },
};

export const STATUS_ORDER = ['todo', 'in-progress', 'done'];
export const LEVELS = ['low', 'medium', 'high'];

/**
 * Is this task parked in the backlog?
 *
 * Deliberately orthogonal to `status` rather than a fourth member of
 * STATUS_ORDER: demoting an in-progress task would otherwise destroy the fact
 * that it was in progress, and STATUS_ORDER is shared vocabulary — a fourth
 * entry would grow a phantom column in the chat mini-board and a phantom row in
 * the bulk menu, both of which iterate it.
 *
 * One predicate, imported everywhere, so "hidden" means the same thing in the
 * eight places that have to agree on it.
 */
export const isDemoted = (task) => !!task?.demoted_at;

export const PRIORITY_META = {
    high: { label: 'High', chip: 'bg-red-500/20 text-red-400 border-red-500/30' },
    medium: { label: 'Medium', chip: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    low: { label: 'Low', chip: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
};

export const IMPORTANCE_META = {
    high: { label: 'High', chip: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    medium: { label: 'Medium', chip: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    low: { label: 'Low', chip: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
};

export const DIFFICULTY_META = {
    high: { label: 'High', chip: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    medium: { label: 'Medium', chip: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
    low: { label: 'Low', chip: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
};

/** Field descriptors used to render the pickers and the changeset diff lines. */
export const TASK_FIELDS = {
    status: { label: 'Status', prefix: '', options: STATUS_ORDER, meta: STATUS_META },
    priority: { label: 'Priority', prefix: 'P', options: LEVELS, meta: PRIORITY_META },
    importance: { label: 'Importance', prefix: 'I', options: LEVELS, meta: IMPORTANCE_META },
    difficulty: { label: 'Difficulty', prefix: 'D', options: LEVELS, meta: DIFFICULTY_META },
};

export const FIELD_LABELS = {
    title: 'title',
    description: 'notes',
    status: 'status',
    priority: 'priority',
    importance: 'importance',
    difficulty: 'difficulty',
    quadrant: 'quadrant',
    due_date: 'due date',
    linked_files: 'linked files',
    demoted_at: 'backlog',
};

export const QUADRANT_LABELS = {
    q1: 'Do First',
    q2: 'Schedule',
    q3: 'Delegate',
    q4: 'Eliminate',
};

/**
 * Eisenhower quadrant from priority (urgency) x importance (impact).
 * Mirrors `derive_quadrant` in backend/task_changes.py.
 */
export function deriveQuadrant(priority, importance) {
    const urgent = priority === 'high';
    const important = importance === 'high';
    if (urgent && important) return 'q1';
    if (important) return 'q2';
    if (urgent) return 'q3';
    return 'q4';
}

/** Human-readable value for a field, used by the changeset diff renderer. */
export function formatFieldValue(field, value) {
    // Above the empty guard on purpose: for this field an absent value is not
    // "nothing", it is "on the board" — the destination of every promote. Left
    // below, a promote would read `backlog  Backlog → —`.
    //
    // A timestamp is an implementation detail of "parked"; the diff line has to
    // read as the move it was, not as `backlog  — → 2026-08-27T10:14:00`.
    if (field === 'demoted_at') return value ? 'Backlog' : 'Board';
    if (value === null || value === undefined || value === '') return '—';
    if (field === 'status') return STATUS_META[value]?.label ?? value;
    if (field === 'quadrant') return QUADRANT_LABELS[value] ?? value;
    // Without this a rescheduling diff — the primary output of the whole agent
    // scheduling path — rendered as `due date  2026-07-24T00:00:00 →
    // 2026-07-31T00:00:00`, which is the one thing in the changeset nobody can
    // read at a glance. Absolute, not relative: "Today → In 2 days" is
    // meaningless when you scroll back to it next week.
    if (field === 'due_date') return formatDay(value) ?? '—';
    if (field === 'linked_files') return `${value.length} file${value.length === 1 ? '' : 's'}`;
    if (typeof value === 'string' && value.length > 60) return `${value.slice(0, 60)}…`;
    if (typeof value === 'string') return value.charAt(0).toUpperCase() + value.slice(1);
    return String(value);
}
